import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React, { Component, type ErrorInfo, type ReactNode } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import "@fontsource/cormorant-garamond/latin-600.css";
import "@fontsource/cormorant-garamond/latin-700.css";
import "@fontsource/libre-baskerville/latin-400.css";
import "@fontsource/libre-baskerville/latin-700.css";
import "@fontsource/inter/latin-400.css";
import "@fontsource/inter/latin-500.css";
import "@fontsource/inter/latin-600.css";
import "@fontsource/inter/latin-700.css";
import "@fontsource/inter/latin-800.css";
import { App } from "./app/App";
import { Button } from "./components/ui";
import { NotificationProvider } from "./components/NotificationCenter";
import { AuthProvider } from "./features/auth/AuthProvider";
import { MobilityProvider } from "./features/mobility/MobilityProvider";
import { I18nProvider, TranslationSurface } from "./features/i18n/I18nProvider";
import "./styles.css";
import "./carousel-minimal.css";
import "./brand-identity.css";

try {
  const savedTheme = window.localStorage.getItem("pby-theme");
  const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  document.documentElement.classList.toggle(
    "dark",
    savedTheme === "dark" || (savedTheme === null && prefersDark),
  );
} catch {
  document.documentElement.classList.toggle(
    "dark",
    window.matchMedia("(prefers-color-scheme: dark)").matches,
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (import.meta.env.DEV) console.error(error, info);

    const isStaleDeployment =
      /dynamically imported module|ChunkLoadError|Loading chunk|Importing a module script failed/i.test(
        `${error.name}: ${error.message}`,
      );

    if (isStaleDeployment) {
      const recoveryKey = "munipiri-deploy-recovery";
      try {
        const lastRecovery = Number(sessionStorage.getItem(recoveryKey) ?? 0);

        if (Date.now() - lastRecovery > 60_000) {
          sessionStorage.setItem(recoveryKey, String(Date.now()));
          window.location.reload();
        }
      } catch {
        window.location.reload();
      }
    }
  }

  resetApplication = () => {
    try {
      window.localStorage.removeItem("pby-theme");
    } catch {
      // El navegador puede bloquear el almacenamiento; la recarga sigue siendo segura.
    }

    try {
      window.sessionStorage.removeItem("munipiri-deploy-recovery");
    } catch {
      // El navegador puede bloquear el almacenamiento; la recarga sigue siendo segura.
    }

    window.location.replace(`/?recovery=${Date.now()}`);
  };

  render() {
    if (this.state.hasError) {
      return (
        <main className="fatal-error">
          <p className="eyebrow">Error de aplicación</p>
          <h1>No pudimos mostrar esta pantalla</h1>
          <p>Puede tratarse de una actualización reciente de la plataforma.</p>
          <div className="fatal-error-actions">
            <Button type="button" onClick={this.resetApplication}>
              Restablecer y recargar
            </Button>
            <a href="/">Volver al inicio</a>
          </div>
        </main>
      );
    }
    return this.props.children;
  }
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <BrowserRouter>
      <I18nProvider>
        <TranslationSurface>
          <ErrorBoundary>
            <QueryClientProvider client={queryClient}>
              <AuthProvider>
                <NotificationProvider>
                  <MobilityProvider>
                    <App />
                  </MobilityProvider>
                </NotificationProvider>
              </AuthProvider>
            </QueryClientProvider>
          </ErrorBoundary>
        </TranslationSurface>
      </I18nProvider>
    </BrowserRouter>
  </React.StrictMode>,
);